// utils.ts

import { KTSelect } from './select';
import { defaultTemplates, Templates } from './templates';
import { KTSelectConfigInterface } from './config';

/**
 * Format a number as a currency string
 */
export function formatCurrency(value: number): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD'
	}).format(value);
}

/**
 * Filter options based on a search query
 */
export function filterOptions(
	options: HTMLElement[],
	query: string,
	config: KTSelectConfigInterface,
	dropdownElement: HTMLElement,
	onVisibleCount?: (count: number) => void
): number {
	let visibleOptionsCount = 0;
	const theme = Templates.getTheme();

	// Clear existing "no results" messages
	const noResultsElement = dropdownElement.querySelector('[data-kt-select-no-results]');
	if (noResultsElement) {
		noResultsElement.remove();
	}

	// Filter options based on query
	for (const option of options) {
		const optionText = option.textContent?.toLowerCase() || '';
		const isMatch = optionText.includes(query);

		if (isMatch || query.trim() === '') {
			option.style.display = 'block';
			visibleOptionsCount++;

			// Apply highlighting if needed - but preserve the option structure
			if (isMatch && config.searchHighlight && query.trim() !== '') {
				// Clone option elements that contain icons or descriptions
				const hasIcon = option.querySelector('.option-icon') !== null;
				const hasDescription = option.querySelector('.option-description') !== null;

				if (hasIcon || hasDescription) {
					// Only highlight the text part without changing structure
					const titleElement = option.querySelector('.option-title') as HTMLElement;
					if (titleElement) {
						const originalText = titleElement.textContent || '';
						highlightTextInElement(titleElement, query, theme.searchHighlight);
					}
				} else {
					// Simple option with just text - standard highlighting
					highlightMatchingText(option, query, theme);
				}
			}
		} else {
			option.style.display = 'none';
		}

		// Early exit if maxItems limit is reached
		if (config.searchMaxItems && visibleOptionsCount >= config.searchMaxItems) {
			break;
		}
	}

	// Handle "no results" state
	if (visibleOptionsCount === 0 && query.trim() !== '') {
		const noResultsElement = defaultTemplates.noResults(config, theme);
		noResultsElement.setAttribute('data-kt-select-no-results', '');

		const optionsContainer = dropdownElement.querySelector('[data-kt-select-options-container]');
		if (optionsContainer) {
			optionsContainer.appendChild(noResultsElement);
		} else {
			dropdownElement.appendChild(noResultsElement);
		}
	}

	// Call the callback with the visible count if provided
	if (onVisibleCount) {
		onVisibleCount(visibleOptionsCount);
	}

	return visibleOptionsCount;
}

/**
 * Highlight text only within a specific element, preserving other elements
 */
export function highlightTextInElement(element: HTMLElement, query: string, highlightClass: string): void {
	if (!element || !query || query.trim() === '') return;

	const originalText = element.textContent || '';
	const queryLower = query.toLowerCase();
	const textLower = originalText.toLowerCase();

	let html = '';
	let lastIndex = 0;

	// Find all occurrences of the query in the text
	let startIndex = textLower.indexOf(queryLower);
	while (startIndex !== -1) {
		// Add text before the match
		html += originalText.substring(lastIndex, startIndex);

		// Add the highlighted match
		const matchEnd = startIndex + query.length;
		const matchedText = originalText.substring(startIndex, matchEnd);
		html += `<span class="${highlightClass}">${matchedText}</span>`;

		// Move to the next position
		lastIndex = matchEnd;
		startIndex = textLower.indexOf(queryLower, lastIndex);
	}

	// Add any remaining text after the last match
	if (lastIndex < originalText.length) {
		html += originalText.substring(lastIndex);
	}

	// Update the element
	element.innerHTML = html;
}

/**
 * Highlight matching text within an option element
 */
export function highlightMatchingText(element: HTMLElement, query: string, theme: any): void {
	if (!element || !query || query.trim() === '') return;

	const optionText = element.textContent || '';
	const searchHighlightClass = theme.searchHighlight;

	// Use the more targeted function
	highlightTextInElement(element, query, searchHighlightClass);
}

/**
 * Focus manager for keyboard navigation
 * Consolidates redundant focus management logic into shared functions
 */
export class FocusManager {
	private _element: HTMLElement;
	private _optionsSelector: string;
	private _focusedOptionIndex: number | null = null;
	private _focusClass: string = 'option-focused';
	private _hoverClass: string = 'hovered';

	constructor(element: HTMLElement, optionsSelector: string = '[data-kt-select-option]') {
		this._element = element;
		this._optionsSelector = optionsSelector;
	}

	/**
	 * Get all visible options
	 */
	public getVisibleOptions(): HTMLElement[] {
		return Array.from(
			this._element.querySelectorAll(this._optionsSelector)
		).filter(option => (option as HTMLElement).style.display !== 'none') as HTMLElement[];
	}

	/**
	 * Focus the next visible option
	 */
	public focusNext(): HTMLElement | null {
		const options = this.getVisibleOptions();
		if (options.length === 0) return null;

		this.resetFocus();

		if (this._focusedOptionIndex === null) {
			this._focusedOptionIndex = 0;
		} else {
			this._focusedOptionIndex = (this._focusedOptionIndex + 1) % options.length;
		}

		const option = options[this._focusedOptionIndex];
		this.applyFocus(option);
		this.scrollIntoView(option);

		return option;
	}

	/**
	 * Focus the previous visible option
	 */
	public focusPrevious(): HTMLElement | null {
		const options = this.getVisibleOptions();
		if (options.length === 0) return null;

		this.resetFocus();

		if (this._focusedOptionIndex === null) {
			this._focusedOptionIndex = options.length - 1;
		} else {
			this._focusedOptionIndex = (this._focusedOptionIndex - 1 + options.length) % options.length;
		}

		const option = options[this._focusedOptionIndex];
		this.applyFocus(option);
		this.scrollIntoView(option);

		return option;
	}

	/**
	 * Apply focus to a specific option
	 */
	public applyFocus(option: HTMLElement): void {
		if (!option) return;

		// Remove focus from all options
		this.resetFocus();

		// Add focus to this option
		option.classList.add(this._focusClass);
		option.classList.add(this._hoverClass);
	}

	/**
	 * Reset focus on all options
	 */
	public resetFocus(): void {
		const focusedOptions = this._element.querySelectorAll(
			`${this._optionsSelector}.${this._focusClass}, ${this._optionsSelector}.${this._hoverClass}`
		);

		focusedOptions.forEach(option => {
			option.classList.remove(this._focusClass);
			option.classList.remove(this._hoverClass);
		});

		// Reset index if visible options have changed
		const visibleOptions = this.getVisibleOptions();
		if (this._focusedOptionIndex !== null && this._focusedOptionIndex >= visibleOptions.length) {
			this._focusedOptionIndex = null;
		}
	}

	/**
	 * Ensure the focused option is visible in the scrollable container
	 */
	public scrollIntoView(option: HTMLElement): void {
		if (!option) return;

		const container = this._element.querySelector('[data-kt-select-options-container]');
		if (!container) return;

		const optionRect = option.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();

		// Check if option is below the visible area
		if (optionRect.bottom > containerRect.bottom) {
			option.scrollIntoView({ block: 'end', behavior: 'smooth' });
		}
		// Check if option is above the visible area
		else if (optionRect.top < containerRect.top) {
			option.scrollIntoView({ block: 'start', behavior: 'smooth' });
		}
	}

	/**
	 * Focus a specific option by its value
	 */
	public focusOptionByValue(value: string): boolean {
		const options = this.getVisibleOptions();
		const index = options.findIndex(option => option.dataset.value === value);

		if (index >= 0) {
			this._focusedOptionIndex = index;
			this.applyFocus(options[index]);
			this.scrollIntoView(options[index]);
			return true;
		}

		return false;
	}

	/**
	 * Get the currently focused option
	 */
	public getFocusedOption(): HTMLElement | null {
		const options = this.getVisibleOptions();

		if (this._focusedOptionIndex !== null && this._focusedOptionIndex < options.length) {
			return options[this._focusedOptionIndex];
		}

		return null;
	}

	/**
	 * Get the index of the currently focused option
	 */
	public getFocusedIndex(): number | null {
		return this._focusedOptionIndex;
	}

	/**
	 * Set the focused option index directly
	 */
	public setFocusedIndex(index: number | null): void {
		this._focusedOptionIndex = index;
	}
}

/**
 * Shared keyboard navigation handler for dropdown options
 * Can be used by both combobox and search modules
 */
export function handleDropdownKeyNavigation(
	event: KeyboardEvent,
	select: KTSelect,
	config: {
		multiple?: boolean,
		closeOnSelect?: boolean
	},
	callbacks?: {
		onArrowUp?: () => void,
		onArrowDown?: () => void,
		onEnter?: () => void,
		onClose?: () => void
	}
): void {
	try {
		// Get the dropdown state
		const isDropdownOpen = (select as any)._dropdownIsOpen;

		// Log the event to help debug
		const origin = 'handleDropdownKeyNavigation';
		console.log(`[${origin}] Key: ${event.key}, Dropdown open: ${isDropdownOpen}`);

		// Handle basic keyboard navigation
		switch (event.key) {
			case 'ArrowDown':
				if (!isDropdownOpen) {
					console.log(`[${origin}] Opening dropdown on ArrowDown`);
					select.openDropdown();

					// Focus the first option after opening
					setTimeout(() => {
						(select as any)._focusNextOption();
					}, 50);
				} else if (callbacks?.onArrowDown) {
					console.log(`[${origin}] Using custom onArrowDown callback`);
					callbacks.onArrowDown();
				} else {
					console.log(`[${origin}] Using default _focusNextOption`);
					const focusedOption = (select as any)._focusNextOption();

					// Ensure we have a focused option
					if (focusedOption) {
						console.log(`[${origin}] Focused next option:`, focusedOption);
					}
				}
				event.preventDefault();
				break;

			case 'ArrowUp':
				if (!isDropdownOpen) {
					console.log(`[${origin}] Opening dropdown on ArrowUp`);
					select.openDropdown();

					// Focus the last option after opening
					setTimeout(() => {
						(select as any)._focusPreviousOption();
					}, 50);
				} else if (callbacks?.onArrowUp) {
					console.log(`[${origin}] Using custom onArrowUp callback`);
					callbacks.onArrowUp();
				} else {
					console.log(`[${origin}] Using default _focusPreviousOption`);
					const focusedOption = (select as any)._focusPreviousOption();

					// Ensure we have a focused option
					if (focusedOption) {
						console.log(`[${origin}] Focused previous option:`, focusedOption);
					}
				}
				event.preventDefault();
				break;

			case 'Enter':
				// Prevent form submission
				event.preventDefault();

				if (isDropdownOpen) {
					console.log(`[${origin}] Enter pressed with dropdown open`);

					// For combobox mode, ensure we update the input value directly
					const isCombobox = select.getConfig().mode === 'combobox';
					const comboboxModule = (select as any)._comboboxModule;

					if (callbacks?.onEnter) {
						console.log(`[${origin}] Using custom onEnter callback`);
						callbacks.onEnter();
					} else {
						console.log(`[${origin}] Using default selectFocusedOption`);
						// Make sure there is a focused option before trying to select it
						if ((select as any)._focusManager && (select as any)._focusManager.getFocusedOption()) {
							select.selectFocusedOption();
						} else {
							// If no option is focused, try to focus the first one
							const focusedOption = (select as any)._focusNextOption();
							// Only select if an option was successfully focused
							if (focusedOption) {
								select.selectFocusedOption();
							}
						}
					}

					// Close dropdown after selection if not multiple and closeOnSelect is true
					if (!config.multiple && config.closeOnSelect !== false) {
						console.log(`[${origin}] Closing dropdown after selection`);
						select.closeDropdown();
					}
				} else {
					// If dropdown is closed, open it on Enter
					console.log(`[${origin}] Opening dropdown on Enter`);
					select.openDropdown();

					// Focus the first option after opening
					setTimeout(() => {
						(select as any)._focusNextOption();
					}, 50);
				}
				break;

			case 'Tab':
				// Only handle tab if dropdown is open
				if (isDropdownOpen) {
					console.log(`[${origin}] Closing dropdown on Tab`);
					select.closeDropdown();
					if (callbacks?.onClose) {
						callbacks.onClose();
					}
					// Don't prevent default tab behavior - let it move focus naturally
				}
				break;

			case 'Escape':
				// Only handle escape if dropdown is open
				if (isDropdownOpen) {
					console.log(`[${origin}] Closing dropdown on Escape`);
					select.closeDropdown();
					if (callbacks?.onClose) {
						callbacks.onClose();
					}
					event.preventDefault(); // Prevent other escape handlers
				}
				break;

			case ' ': // Space key
				// If dropdown is closed, space should open it (but not if in combobox mode)
				if (!isDropdownOpen && !(select.getConfig().mode === 'combobox')) {
					console.log(`[${origin}] Opening dropdown on Space`);
					select.openDropdown();

					// Focus the first option after opening
					setTimeout(() => {
						(select as any)._focusNextOption();
					}, 50);

					event.preventDefault();
				}
				break;
		}
	} catch (error) {
		console.error('Error in keyboard navigation handler:', error);
	}
}

/**
 * Centralized event listener management
 */
export class EventManager {
	private _boundHandlers: Map<string, Map<EventListenerOrEventListenerObject, EventListenerOrEventListenerObject>> = new Map();

	/**
	 * Add an event listener with a bound context
	 */
	public addListener(
		element: HTMLElement,
		event: string,
		handler: EventListenerOrEventListenerObject,
		context?: any
	): void {
		if (!element) return;

		// Create a bound version of the handler if context provided
		const boundHandler: EventListenerOrEventListenerObject = context && typeof handler === 'function'
			? handler.bind(context)
			: handler;

		// Store the relationship between original and bound handler
		if (!this._boundHandlers.has(event)) {
			this._boundHandlers.set(event, new Map());
		}

		const eventMap = this._boundHandlers.get(event)!;
		eventMap.set(handler, boundHandler);

		// Add the event listener
		element.addEventListener(event, boundHandler);
	}

	/**
	 * Remove an event listener
	 */
	public removeListener(
		element: HTMLElement,
		event: string,
		handler: EventListenerOrEventListenerObject
	): void {
		if (!element) return;

		const eventMap = this._boundHandlers.get(event);
		if (!eventMap) return;

		// Get the bound version of the handler
		const boundHandler = eventMap.get(handler);
		if (!boundHandler) return;

		// Remove the event listener
		element.removeEventListener(event, boundHandler);

		// Clean up the map
		eventMap.delete(handler);
		if (eventMap.size === 0) {
			this._boundHandlers.delete(event);
		}
	}

	/**
	 * Remove all event listeners
	 */
	public removeAllListeners(element: HTMLElement): void {
		if (!element) return;

		// Go through each event type
		this._boundHandlers.forEach((eventMap, event) => {
			// For each event type, go through each handler
			eventMap.forEach((boundHandler, originalHandler) => {
				element.removeEventListener(event, boundHandler);
			});
		});

		// Clear the maps
		this._boundHandlers.clear();
	}
}

/**
 * Debounce function to limit how often a function can be called
 */
export function debounce(func: (...args: any[]) => void, delay: number): (...args: any[]) => void {
	let timeout: ReturnType<typeof setTimeout>;

	return function(...args: any[]) {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), delay);
	};
}